// src/components/Auth/Signup.js
import React, { useState, useEffect, useContext } from 'react';
import { Form, Alert } from 'react-bootstrap';
import { AuthContext } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import useNotification from '../../hooks/useNotification';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { getGoogleOAuthRedirectUri } from '../../utils/env';
import { AFTER_LOGIN_PATH } from '../../constants/routes';
import PasswordChecklist from './PasswordChecklist';
import { mapPasswordError, mapEmailError } from '../../utils/passwordRules';
import { Info } from 'lucide-react';
import FroggySpinner from '../common/FroggySpinner';
import '../../styles/login.css';
import '../../styles/password-tooltip.css';
import '../../styles/password-checklist.css';

const Signup = () => {
  const { signup, loginWithGoogle, isAuthenticated, authLoading } = useContext(AuthContext);
  const cart = useCart();
  const cartItems = cart?.cartItems || [];
  const { showNotification } = useNotification();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({ email: null, password: null, confirmPassword: null });
  const [checklistState, setChecklistState] = useState({ isOpen: false, trigger: null }); // 'input' | 'icon' | null
  const passwordInputRef = React.useRef(null);
  const infoIconRef = React.useRef(null);
  const checklistPanelRef = React.useRef(null);
  const passwordChecklistId = 'password-requirements';
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({ email: null, password: null, confirmPassword: null });

    // Client-side validation
    if (password !== confirmPassword) {
      setFieldErrors({ ...fieldErrors, confirmPassword: "Passwords don't match." });
      return;
    }

    try {
      await signup(email, password, cartItems, () => {
        cart.setCartMerged(false);
        navigate('/email-confirmation-sent');
      });
    } catch (err) {
      const data = err.response?.data;

      // Handle Google login required case
      if (data?.code === 'google_login_required') {
        setError('Please use "Sign up with Google" — this account was created using Google login.');
        return;
      }

      // Parse field-specific errors from backend
      const newFieldErrors = { email: null, password: null, confirmPassword: null };
      let hasFieldErrors = false;

      // Check for email errors
      if (data?.email) {
        const emailError = Array.isArray(data.email) ? data.email[0] : data.email;
        newFieldErrors.email = mapEmailError(emailError);
        hasFieldErrors = true;
      }

      // Check for password errors (password1 or password)
      const passwordErrors = data?.password1 || data?.password;
      if (passwordErrors) {
        const passwordError = Array.isArray(passwordErrors) ? passwordErrors[0] : passwordErrors;
        newFieldErrors.password = mapPasswordError(passwordError);
        hasFieldErrors = true;
      }

      // Check for password2/confirmPassword errors
      if (data?.password2) {
        const confirmError = Array.isArray(data.password2) ? data.password2[0] : data.password2;
        newFieldErrors.confirmPassword = mapPasswordError(confirmError);
        hasFieldErrors = true;
      }

      // Check for non-field errors
      if (data?.non_field_errors) {
        const nonFieldError = Array.isArray(data.non_field_errors)
          ? data.non_field_errors[0]
          : data.non_field_errors;

        // If it's a password mismatch, show it on confirmPassword field
        if (nonFieldError.toLowerCase().includes("match")) {
          newFieldErrors.confirmPassword = mapPasswordError(nonFieldError);
          hasFieldErrors = true;
        } else {
          setError(nonFieldError);
        }
      }

      if (hasFieldErrors) {
        setFieldErrors(newFieldErrors);
      } else if (data?.detail) {
        setError(data.detail);
      } else {
        setError("An unexpected error occurred.");
      }
    }
  };

  const handleGoogleSignup = async (response) => {
    try {
      const id_token = response.credential;
      if (!id_token) {
        setError("Google signup failed: missing credential.");
        return;
      }
      await loginWithGoogle(
        id_token,
        'signup',
        cartItems,
        () => {
          cart.setCartMerged(false);
          setTimeout(() => {
            // Compute target: prioritize location state, then default
            const from = location.state?.from?.pathname;
            const target = from && from !== '/login' && from !== '/signup'
              ? from
              : AFTER_LOGIN_PATH;

            navigate(target, { replace: true });
          }, 100);
        },
        getGoogleOAuthRedirectUri()
      );
    } catch (err) {
      const data = err.response?.data;
      const backendError =
        data?.error ||
        data?.detail ||
        (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]);
      const errorMessage = backendError || 'An unexpected error occurred during Google signup.';
      console.error('[Signup] Google failed:', { status: err.response?.status, backendError, message: err.message });
      setError(errorMessage);
    }
  };

  // Handle ESC key and outside clicks to close checklist
  useEffect(() => {
    if (!checklistState.isOpen) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setChecklistState({ isOpen: false, trigger: null });
      }
    };

    const handleClickOutside = (e) => {
      const clickedIcon = infoIconRef.current?.contains(e.target);
      const clickedInput = passwordInputRef.current?.contains(e.target);
      const clickedPanel = checklistPanelRef.current?.contains(e.target);

      if (!clickedIcon && !clickedInput && !clickedPanel) {
        setChecklistState({ isOpen: false, trigger: null });
      }
    };

    document.addEventListener('keydown', handleEsc);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [checklistState.isOpen]);

  // Handle info icon interactions
  const handleIconClick = () => {
    setChecklistState({ isOpen: true, trigger: 'icon' });
    passwordInputRef.current?.focus();
  };

  const handleIconMouseEnter = () => {
    setChecklistState({ isOpen: true, trigger: 'icon' });
  };

  const handleIconMouseLeave = (e) => {
    // Don't close if moving to the panel
    const relatedTarget = e.relatedTarget;
    if (checklistPanelRef.current?.contains(relatedTarget)) {
      return;
    }
    // Give a small delay to allow moving to panel
    setTimeout(() => {
      if (checklistState.trigger === 'icon') {
        setChecklistState({ isOpen: false, trigger: null });
      }
    }, 100);
  };

  return (
    <div className="login-form">
      <h2>Signup</h2>
      {error && <Alert variant="danger">{error}</Alert>}
      {authLoading && (
        <div className="text-center my-3">
          <FroggySpinner size="md" />
        </div>
      )}
      <Form onSubmit={handleSubmit}>
        <Form.Group controlId="formEmail" className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            placeholder="Enter email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (fieldErrors.email) {
                setFieldErrors({ ...fieldErrors, email: null });
              }
            }}
            isInvalid={!!fieldErrors.email}
          />
          {fieldErrors.email && (
            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
              {fieldErrors.email}
            </Form.Control.Feedback>
          )}
        </Form.Group>

        <Form.Group controlId="formPassword" className="mb-3">
          <Form.Label>
            Password
            <span className="password-tooltip-container">
              <button
                ref={infoIconRef}
                type="button"
                className="password-tooltip-trigger"
                onClick={handleIconClick}
                onMouseEnter={handleIconMouseEnter}
                onMouseLeave={handleIconMouseLeave}
                aria-label="Password requirements"
                aria-controls={passwordChecklistId}
                aria-expanded={checklistState.isOpen && checklistState.trigger === 'icon'}
              >
                <Info size={16} />
              </button>
            </span>
          </Form.Label>
          <Form.Control
            ref={passwordInputRef}
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (fieldErrors.password) {
                setFieldErrors({ ...fieldErrors, password: null });
              }
            }}
            onFocus={() => setChecklistState({ isOpen: true, trigger: 'input' })}
            onBlur={(e) => {
              // Don't close if focus moved to icon or panel
              const relatedTarget = e.relatedTarget;
              if (!infoIconRef.current?.contains(relatedTarget) &&
                  !checklistPanelRef.current?.contains(relatedTarget)) {
                setChecklistState({ isOpen: false, trigger: null });
              }
            }}
            isInvalid={!!fieldErrors.password}
            aria-describedby={passwordChecklistId}
          />
          {fieldErrors.password && (
            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
              {fieldErrors.password}
            </Form.Control.Feedback>
          )}
          <PasswordChecklist
            password={password}
            show={checklistState.isOpen && !fieldErrors.password}
            inputRef={passwordInputRef}
            anchorRef={checklistState.trigger === 'icon' ? infoIconRef : passwordInputRef}
            panelRef={checklistPanelRef}
            id={passwordChecklistId}
          />
        </Form.Group>

        <Form.Group controlId="formConfirmPassword" className="mb-3" style={{ marginTop: '1rem' }}>
          <Form.Label>Confirm Password</Form.Label>
          <Form.Control
            type="password"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (fieldErrors.confirmPassword) {
                setFieldErrors({ ...fieldErrors, confirmPassword: null });
              }
            }}
            isInvalid={!!fieldErrors.confirmPassword}
          />
          {fieldErrors.confirmPassword && (
            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
              {fieldErrors.confirmPassword}
            </Form.Control.Feedback>
          )}
        </Form.Group>

        <button type="submit" className="ff-btn ff-btn--primary ff-btn--block" disabled={authLoading}>
          {authLoading ? 'Signing up...' : 'Signup'}
        </button>
      </Form>
      <div className="social-login mt-4">
      <GoogleLogin
        onSuccess={handleGoogleSignup}
        onError={() => setError('Google signup failed.')}
        useOneTap={false}
        ux_mode="popup"
        type="standard"
        scope="openid profile email"
        />
      </div>
      <div className="signup-redirect mt-3 text-center">
        <span>
          Already have an account?{' '}
          <span className="signup-link" onClick={() => navigate('/login')} role="button">
            Log in
          </span>
        </span>
      </div>
    </div>
  );
};

export default Signup;
